// Ayarlar — yalnızca yönetici. Dört sekme: kullanıcılar, temsilci kayıtları, eşikler,
// durum kategorileri. Her mutasyondan sonra loadSettings() ile dördü birden tazelenir.
// (Panel Şifreleri kendi sayfasıdır — pages/PanelPasswords.tsx; yalnız admin, görev #187/#188.)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  addAgentAlias,
  createUser,
  getAgentRecords,
  getLazinessConfig,
  getScheduleState,
  getThresholds,
  getUsers,
  mergeAgent,
  removeAgentAlias,
  offboardAgentRecord,
  resetPassword,
  restoreAgentRecord,
  setLazinessIncluded,
  syncAgentPanel,
  syncSchedule,
  updateAgentRecord,
  updateThresholds,
  updateUser,
} from '../api';
import { filterAgents } from '../agent-filter';
import { ALL_BRANDS, DEPARTMENTS, brandLabel, tzOfBrands } from '../constants';
import { fmtStamp, joinDot, todayInTz } from '../format';
import { useEscape, useExpanded } from '../hooks';
import { BrandSteps } from './NewHire';
import type {
  AgentRecord,
  BrandId,
  Lang,
  LazinessConfig,
  LazinessEntry,
  OffboardReason,
  PanelSyncResult,
  PanelSyncStep,
  PanelUser,
  Role,
  ScheduleState,
  StatusKey,
  Thresholds,
} from '../types';
import {
  Icon,
  Modal,
  Select,
  Skeleton,
  cardStyle,
  checkboxStyle,
  chipStyle,
  dotStyle,
  ghostBtnStyle,
  inputStyle,
  knobStyle,
  primaryBtnStyle,
  segStyle,
  switchStyle,
} from '../ui';

type StTab = 'users' | 'agents' | 'thresholds' | 'statuses';
type UmMode = 'new' | 'edit' | 'reset';

const TABS: [StTab, string, string][] = [
  ['users', 'Kullanıcılar', 'group'],
  ['agents', 'Temsilciler', 'support_agent'],
  ['thresholds', 'Eşikler', 'tune'],
  ['statuses', 'Durum Kategorileri', 'checklist'],
];

/** [anahtar, etiket, birim, varsayılan] — eşik alanı boşsa varsayılan gösterilir. */
const TH_ROWS: [keyof Thresholds, string, string, number][] = [
  ['idleBeforeBreakMin', 'Mola öncesi boşta eşiği', 'dk', 5],
  ['darkDiff', 'DARK fark eşiği', 'çağrı', 20],
  ['longGapMin', 'Uzun boşluk eşiği', 'dk', 30],
  ['dailyBreakMin', 'Günlük mola hakkı', 'dk', 60],
  ['breakGraceMin', 'Mola aşımı toleransı', 'dk', 5],
  ['noShowGraceMin', 'Vardiyaya gelmedi toleransı', 'dk', 60],
  // cagri_hedefi_alti / dusuk_yanit_orani (migration 031) — 0 kuralı kapatır.
  ['dailyCallTarget', 'Günlük çağrı hedefi', 'çağrı', 300],
  ['minAnswerRatePct', 'Yanıtlanma oranı alt sınırı', '%', 15],
  ['dailyTargetGraceMin', 'Günlük hedef değerlendirme payı', 'dk', 60],
];

/** Boş dizge = departman atanmamış. Seçenek metni gerekir (boş bir <option> arızalı
 *  görünür) ama KAYDEDİLEN değer boş kalır — panelde hiçbir yer tutucu basılmaz. */
const DEPT_OPTIONS = ['', ...DEPARTMENTS];
const DEPT_NONE_LABEL = 'Departman yok';

const AGENT_GRID = '1fr 96px 150px 150px 80px 36px 44px';
const USER_GRID = '1fr 120px 1.4fr 90px 150px';

// ── Mutasyon hataları ────────────────────────────────────────────────────────

/** Sunucu mutasyonları YUMUŞAK hata döner: HTTP 200 + `{ ok:false, error:'<TR mesaj>' }`
 *  (src/api/settings.js — kullanıcı adı çakışması, DARK KOD/alias sahiplik çakışması…).
 *  Bunlar yutulursa modal kapanır ve hiçbir şey değişmez; her mutasyon sonucu bu yardımcıdan
 *  geçer: null = başarılı, aksi halde ekrana basılacak TR mesaj.
 *
 *  try/catch ŞART: api.ts'in http()'si ağ kopmasında / 5xx'te FIRLATIR — yakalanmazsa
 *  busy bayrağı asılı kalır ve düğme sayfa yenilenene kadar kilitlenir
 *  (SchedulePlanCard.refresh'teki aynı tuzak). */
const FALLBACK_ERROR = 'İşlem başarısız';

type Mutation = { ok: boolean; error?: string };

async function runMutation(fn: () => Promise<Mutation>): Promise<string | null> {
  try {
    const res = await fn();
    return res.ok ? null : res.error || FALLBACK_ERROR;
  } catch {
    return FALLBACK_ERROR;
  }
}

/** Satır/modal altı hata şeridi. Metin TEK ifade olarak basılır (i18n metin düğümü başına
 *  çalışır); Türkçe karşılıkları web/src/i18n.ts'te. */
function ErrorLine({ text, style }: { text: string; style?: CSSProperties }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: '12px',
        color: '#ef5d5d',
        ...style,
      }}
    >
      <Icon name="error" size={15} />
      {text}
    </div>
  );
}

// ── Kullanıcılar ─────────────────────────────────────────────────────────────

function UsersTab({
  users,
  rowMsg,
  onOpenNew,
  onToggleActive,
  onEdit,
  onReset,
}: {
  users: PanelUser[];
  rowMsg: Record<string, string>;
  onOpenNew: () => void;
  onToggleActive: (u: PanelUser) => void;
  onEdit: (u: PanelUser) => void;
  onReset: (u: PanelUser) => void;
}) {
  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '15px 18px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Panel Kullanıcıları</div>
          <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Erişim, rol ve marka atamaları</div>
        </div>
        <button
          onClick={onOpenNew}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            padding: '9px 14px',
            background: 'var(--accent)',
            border: 'none',
            borderRadius: '9px',
            color: '#fff',
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Icon name="person_add" size={17} />
          Yeni Kullanıcı
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: USER_GRID,
          gap: '12px',
          padding: '9px 18px',
          borderBottom: '1px solid var(--border)',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '.04em',
          color: 'var(--faint)',
        }}
      >
        <span>KULLANICI</span>
        <span>ROL</span>
        <span>MARKALAR</span>
        <span style={{ textAlign: 'center' }}>AKTİF</span>
        <span style={{ textAlign: 'right' }}>İŞLEM</span>
      </div>
      {users.map((u) => (
        // Dış sarmalayıcı satır ayıracını taşır; hata şeridi (varsa) aynı ayıracın İÇİNDE
        // kalsın diye. Hata yokken görsel çıktı tek div'li haliyle birebir aynı.
        <div key={u.username} style={{ borderBottom: '1px solid var(--border)' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: USER_GRID,
              gap: '12px',
              padding: '12px 18px',
              alignItems: 'center',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{u.username}</div>
              <div style={{ fontSize: '11px', color: 'var(--dim)' }}>{u.displayName}</div>
            </div>
            <span style={{ display: 'flex' }}>
              <span
                style={{
                  fontSize: '10.5px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  color: u.role === 'admin' ? '#3d8bff' : 'var(--dim)',
                  background: u.role === 'admin' ? 'rgba(61,139,255,.14)' : 'var(--surface2)',
                  border: '1px solid ' + (u.role === 'admin' ? 'var(--accent-border)' : 'var(--border)'),
                }}
              >
                {u.roleLabel}
              </span>
            </span>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {u.brands.map((b) => (
                <span
                  key={b}
                  style={{
                    fontSize: '10.5px',
                    fontWeight: 600,
                    color: 'var(--dim)',
                    background: 'var(--surface2)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    padding: '2px 8px',
                  }}
                >
                  {brandLabel(b)}
                </span>
              ))}
            </div>
            <span style={{ display: 'flex', justifyContent: 'center' }}>
              <div onClick={() => onToggleActive(u)} style={switchStyle(u.active)}>
                <div style={knobStyle(u.active)} />
              </div>
            </span>
            <div style={{ display: 'flex', gap: '7px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => onEdit(u)}
                title="Düzenle"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--dim)',
                  cursor: 'pointer',
                }}
              >
                <Icon name="edit" size={17} />
              </button>
              <button
                onClick={() => onReset(u)}
                title="Şifre sıfırla"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--dim)',
                  cursor: 'pointer',
                }}
              >
                <Icon name="key" size={17} />
              </button>
            </div>
          </div>
          {rowMsg[u.username] ? (
            <ErrorLine text={rowMsg[u.username]} style={{ padding: '0 18px 11px' }} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ── Temsilciler ──────────────────────────────────────────────────────────────────

function AgentsTab({
  agents: allAgents,
  brands,
  allBrands,
  rowMsg,
  isOpen,
  onToggleExpand,
  onDept,
  onToggleActive,
  onToggleTeamLead,
  onToggleBrand,
  panelSync,
  panelSyncBusy,
  onPanelSync,
  renameInput,
  onRenameInput,
  onRename,
  onConfirm,
  aliasInput,
  onAliasInput,
  onAddAlias,
  onRemoveAlias,
  onMerge,
  onRemove,
  onRestore,
}: {
  agents: AgentRecord[];
  /** Üstteki marka seçimi (App) — yalnız Hera seçiliyse yalnız Hera'nın temsilcileri. */
  brands: BrandId[];
  /** Erişilebilir markaların tamamı: hepsi seçiliyse markasız / BetBooker temsilcisi de görünür. */
  allBrands: BrandId[];
  rowMsg: Record<string, string>;
  isOpen: (id: string) => boolean;
  onToggleExpand: (id: string) => void;
  onDept: (id: string, dept: string) => void;
  onToggleActive: (id: string) => void;
  onToggleTeamLead: (id: string) => void;
  onToggleBrand: (id: string, brand: BrandId) => void;
  /** Son panel hesabı eşitlemesinin sonucu (marka değişikliği ya da "Panel hesabını eşitle"). */
  panelSync: Record<string, PanelSyncResult>;
  panelSyncBusy: string | null;
  onPanelSync: (id: string) => void;
  renameInput: Record<string, string>;
  onRenameInput: (id: string, val: string) => void;
  onRename: (id: string, name: string) => void;
  onConfirm: (id: string) => void;
  aliasInput: Record<string, string>;
  onAliasInput: (key: string, val: string) => void;
  onAddAlias: (id: string, brand: BrandId) => void;
  onRemoveAlias: (id: string, brand: BrandId, name: string) => void;
  onMerge: (a: AgentRecord) => void;
  onRemove: (a: AgentRecord) => void;
  onRestore: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const agents = useMemo(
    () => filterAgents(allAgents, { selected: brands, available: allBrands, query: search, removed: showRemoved }),
    [allAgents, brands, allBrands, search, showRemoved],
  );
  // Çipteki sayı aramadan bağımsız: seçili markalarda kaç kaldırılmış kayıt var.
  const removedCount = useMemo(
    () => filterAgents(allAgents, { selected: brands, available: allBrands, query: '', removed: true }).length,
    [allAgents, brands, allBrands],
  );
  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '15px 18px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Temsilci Kayıtları</div>
          <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>
            {showRemoved
              ? 'Kaldırılan temsilciler — geçmişleri duruyor, geri alınabilir'
              : 'Sarı satırlar otomatik oluşan geçici kayıtlardır'}
          </div>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Temsilci ara"
          style={{ ...inputStyle, maxWidth: '240px', padding: '8px 11px', fontSize: '12.5px' }}
        />
        <button onClick={() => setShowRemoved((v) => !v)} style={segStyle(showRemoved)}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
            <Icon name="person_off" size={16} />
            <span>Kaldırılanlar</span>
            {removedCount > 0 && <span style={{ color: 'var(--faint)' }}>{removedCount}</span>}
          </span>
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '800px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: AGENT_GRID,
              gap: '10px',
              padding: '9px 18px',
              borderBottom: '1px solid var(--border)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.04em',
              color: 'var(--faint)',
            }}
          >
            <span>AD</span>
            <span>DARK KOD</span>
            <span>DEPARTMAN</span>
            <span>MARKALAR</span>
            <span style={{ textAlign: 'center' }}>AKTİF</span>
            <span />
            <span />
          </div>
          {agents.length === 0 && (
            <div style={{ padding: '22px 18px', fontSize: '12.5px', color: 'var(--faint)', textAlign: 'center' }}>
              {showRemoved ? 'Kaldırılmış temsilci yok' : 'Eşleşen temsilci yok'}
            </div>
          )}
          {agents.map((a) => (
            <div
              key={a.id}
              style={{
                borderBottom: '1px solid var(--border)',
                background: a.temp ? 'rgba(224,162,60,.06)' : 'transparent',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: AGENT_GRID,
                  gap: '10px',
                  padding: '11px 18px',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{a.name}</span>
                  {a.temp && (
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        letterSpacing: '.03em',
                        color: '#e0a23c',
                        background: 'rgba(224,162,60,.14)',
                        border: '1px solid rgba(224,162,60,.3)',
                        borderRadius: '5px',
                        padding: '1px 5px',
                        flex: 'none',
                      }}
                    >
                      geçici
                    </span>
                  )}
                  {a.teamLead && (
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        letterSpacing: '.03em',
                        color: 'var(--accent-text2)',
                        background: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        borderRadius: '5px',
                        padding: '1px 5px',
                        flex: 'none',
                      }}
                    >
                      takım lideri
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                  {a.darkCode}
                </span>
                <span style={{ position: 'relative' }}>
                  <select
                    value={a.department}
                    onChange={(e) => onDept(a.id, e.target.value)}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      width: '100%',
                      background: 'var(--inset)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '6px 26px 6px 9px',
                      color: 'var(--text)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  >
                    {DEPT_OPTIONS.map((d) => (
                      <option key={d} value={d} style={{ background: 'var(--surface)' }}>
                        {d || DEPT_NONE_LABEL}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="expand_more"
                    size={15}
                    color="var(--faint)"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                    }}
                  />
                </span>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {a.brands.map((b) => (
                    <span
                      key={b}
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: 'var(--dim)',
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: '5px',
                        padding: '2px 7px',
                      }}
                    >
                      {brandLabel(b)}
                    </span>
                  ))}
                </div>
                <span style={{ display: 'flex', justifyContent: 'center' }}>
                  {a.removed ? (
                    <button
                      onClick={() => onRestore(a.id)}
                      style={{
                        padding: '5px 10px',
                        background: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        borderRadius: '8px',
                        color: 'var(--accent-text2)',
                        fontSize: '11.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Geri al
                    </button>
                  ) : (
                    <div onClick={() => onToggleActive(a.id)} style={switchStyle(a.active)}>
                      <div style={knobStyle(a.active)} />
                    </div>
                  )}
                </span>
                {a.removed ? (
                  <span />
                ) : (
                  <button
                    onClick={() => onRemove(a)}
                    title="İşten ayrıldı"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                  >
                    <Icon name="delete" size={18} color="var(--faint)" />
                  </button>
                )}
                <button
                  onClick={() => onToggleExpand(a.id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                >
                  <Icon name={isOpen(a.id) ? 'expand_more' : 'chevron_right'} size={20} color="var(--faint)" />
                </button>
              </div>
              {/* Satır kapalıyken de görünür: hata alias/rename gibi AÇIK panel işlemlerinden
                  de, departman/marka/aktif gibi KAPALI satır işlemlerinden de gelebilir. */}
              {rowMsg[a.id] ? <ErrorLine text={rowMsg[a.id]} style={{ padding: '0 18px 11px' }} /> : null}
              {isOpen(a.id) && (
                <div style={{ padding: '6px 18px 16px', background: 'var(--row)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dim)', marginBottom: '8px', letterSpacing: '.04em' }}>
                    AD (İSİM)
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      value={renameInput[a.id] ?? a.name}
                      onChange={(e) => onRenameInput(a.id, e.target.value)}
                      style={{
                        background: 'var(--inset)',
                        border: '1px solid var(--border)',
                        borderRadius: '7px',
                        padding: '6px 10px',
                        color: 'var(--text)',
                        fontSize: '12.5px',
                        outline: 'none',
                        width: '200px',
                      }}
                    />
                    <button
                      onClick={() => onRename(a.id, renameInput[a.id] ?? a.name)}
                      style={{
                        padding: '7px 14px',
                        background: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        borderRadius: '8px',
                        color: 'var(--accent-text2)',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Kaydet
                    </button>
                  </div>
                  {/* Takım lideri: raiseAlert'in TEK kapısı (migration 013). Açıldığında
                      sunucu o temsilcinin AÇIK uyarılarını da kapatır. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <div onClick={() => onToggleTeamLead(a.id)} style={switchStyle(a.teamLead)}>
                      <div style={knobStyle(a.teamLead)} />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
                      Takım lideri: bu temsilciden hiçbir uyarı üretilmez
                    </span>
                  </div>
                  {a.temp && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        marginBottom: '12px',
                        background: 'rgba(224,162,60,.08)',
                        border: '1px solid rgba(224,162,60,.28)',
                        borderRadius: '9px',
                      }}
                    >
                      <Icon name="merge" size={18} color="#e0a23c" />
                      <span style={{ flex: 1, fontSize: '12px', color: 'var(--dim)' }}>
                        Bu geçici kayıt, sheet'te eşleşmeyen bir addan otomatik oluştu.
                      </span>
                      <button
                        onClick={() => onConfirm(a.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '7px 12px',
                          background: 'rgba(76,175,102,.12)',
                          border: '1px solid rgba(76,175,102,.34)',
                          borderRadius: '8px',
                          color: '#6fce8a',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Gerçek temsilci, onayla
                      </button>
                      <button
                        onClick={() => onMerge(a)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '7px 12px',
                          background: 'var(--accent-soft)',
                          border: '1px solid var(--accent-border)',
                          borderRadius: '8px',
                          color: 'var(--accent-text2)',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Mevcut temsilciyle birleştir
                      </button>
                    </div>
                  )}
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dim)', marginBottom: '8px', letterSpacing: '.04em' }}>
                    MARKA ATAMASI
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    {ALL_BRANDS.map((b) => (
                      <button key={b.id} onClick={() => onToggleBrand(a.id, b.id)} style={chipStyle(a.brands.includes(b.id))}>
                        {b.name}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dim)', letterSpacing: '.04em' }}>PANEL HESABI</div>
                    <button
                      onClick={() => onPanelSync(a.id)}
                      disabled={panelSyncBusy === a.id}
                      title="Giriş adı, şifre ve dahili seçili markaların paneline taşınır; ayrıldığı markadan kaldırılır"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '4px 10px',
                        background: 'transparent',
                        border: '1px solid var(--border)',
                        borderRadius: '7px',
                        color: 'var(--dim)',
                        fontSize: '11.5px',
                        cursor: panelSyncBusy === a.id ? 'default' : 'pointer',
                        opacity: panelSyncBusy === a.id ? 0.6 : 1,
                      }}
                    >
                      <Icon name="sync" size={14} />
                      {panelSyncBusy === a.id ? 'Eşitleniyor…' : 'Panel hesabını eşitle'}
                    </button>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    {panelSync[a.id] ? (
                      <BrandSteps steps={panelSync[a.id].steps} />
                    ) : (
                      <div style={{ fontSize: '11.5px', color: 'var(--faint)', lineHeight: 1.45 }}>
                        Marka değişince temsilcinin panel girişi (ad, şifre, dahili) yeni markaya taşınır ve ayrıldığı markadan kaldırılır.
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dim)', marginBottom: '8px', letterSpacing: '.04em' }}>
                    TERMİNAL AD EŞLEŞMELERİ (ALIAS)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {a.brands.map((b) => (
                      <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint)', width: '80px', flex: 'none', paddingTop: '6px' }}>
                          {brandLabel(b)}
                        </span>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
                          {(a.aliases[b] || []).map((n) => (
                            <span
                              key={n}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '11.5px',
                                color: 'var(--text)',
                                background: 'var(--inset)',
                                border: '1px solid var(--border)',
                                borderRadius: '7px',
                                padding: '4px 8px',
                              }}
                            >
                              {n}
                              <span
                                onClick={() => onRemoveAlias(a.id, b, n)}
                                style={{
                                  fontFamily: "'Material Symbols Outlined'",
                                  fontSize: '14px',
                                  color: 'var(--faint)',
                                  cursor: 'pointer',
                                  lineHeight: 1,
                                }}
                              >
                                close
                              </span>
                            </span>
                          ))}
                          <input
                            value={aliasInput[a.id + '|' + b] || ''}
                            onChange={(e) => onAliasInput(a.id + '|' + b, e.target.value)}
                            placeholder="+ alias ekle"
                            style={{
                              background: 'var(--inset)',
                              border: '1px solid var(--border)',
                              borderRadius: '7px',
                              padding: '5px 9px',
                              color: 'var(--text)',
                              fontSize: '11.5px',
                              outline: 'none',
                              width: '120px',
                            }}
                          />
                          <button
                            onClick={() => onAddAlias(a.id, b)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              background: 'var(--accent-soft)',
                              border: '1px solid var(--accent-border)',
                              borderRadius: '7px',
                              color: 'var(--accent)',
                              cursor: 'pointer',
                            }}
                          >
                            <Icon name="add" size={15} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Eşikler ──────────────────────────────────────────────────────────────────

function ThresholdsTab({
  thresholds,
  onChange,
}: {
  thresholds: Thresholds;
  onChange: (key: keyof Thresholds, value: string) => void;
}) {
  return (
    <div style={{ ...cardStyle, padding: '18px 20px', maxWidth: '560px' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>Eşik Değerleri</div>
      <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '18px' }}>
        Uyarı ve metrik hesaplamalarında kullanılır
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {TH_ROWS.map(([key, label, unit, def]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ flex: 1, fontSize: '13px', color: 'var(--text)' }}>{label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="number"
                min="0"
                value={thresholds[key] != null ? thresholds[key] : def}
                onChange={(e) => onChange(key, e.target.value)}
                style={{
                  width: '84px',
                  background: 'var(--inset)',
                  border: '1px solid var(--border)',
                  borderRadius: '9px',
                  padding: '9px 11px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontWeight: 600,
                  outline: 'none',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
              <span style={{ fontSize: '12px', color: 'var(--dim)', width: '38px' }}>{unit}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vardiya planı ────────────────────────────────────────────────────────────

/** Plan senkron durumu + elle yenileme. Sheet canlı bir insan belgesi: gün ortasında
 *  vardiya takas edilirse ertesi günkü otomatik okumayı beklemek gerekmesin. */
function SchedulePlanCard() {
  const [state, setState] = useState<ScheduleState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setState(await getScheduleState());
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    setMsg('');
    // finally, not a bare await: syncSchedule() THROWS on a network drop / 5xx (api.ts http()),
    // and an un-cleared `busy` would disable the button until the page is reloaded.
    try {
      const res = await syncSchedule();
      if (res.ok) {
        setMsg('Plan yenilendi');
        await load();
      } else {
        setMsg(res.error || 'Yenileme başarısız');
      }
    } catch {
      setMsg('Yenileme başarısız');
    } finally {
      setBusy(false);
    }
  };

  if (!state || !state.enabled || state.brands.length === 0) return null;

  return (
    <div style={{ ...cardStyle, padding: '18px 20px', maxWidth: '560px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Vardiya Planı</span>
        <button
          onClick={() => void refresh()}
          disabled={busy}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: '9px',
            padding: '7px 12px',
            color: 'var(--dim)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          <Icon name="refresh" size={15} />
          Şimdi yenile
        </button>
      </div>
      <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '16px' }}>
        {'“' + state.tab + '” sekmesinden her gün ' + state.syncAt + ' (İstanbul) okunur'}
      </div>

      {state.brands.map((b) => {
        const s = state.state[b] || {};
        return (
          <div key={b} style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{brandLabel(b)}</span>
              {s.staleWeek && (
                <span
                  style={{
                    fontSize: '10.5px',
                    fontWeight: 600,
                    color: '#e0a23c',
                    background: 'rgba(224,162,60,.14)',
                    border: '1px solid rgba(224,162,60,.4)',
                    borderRadius: '6px',
                    padding: '2px 7px',
                  }}
                >
                  Bu haftaya güncellenmemiş
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px', color: 'var(--dim)' }}>
              {/* Değeri olmayan etiket hiç basılmaz (yer tutucu yok) — henüz senkron
                  olmamış bir marka "Son okuma:" diye sarkık bir satır bırakmasın. */}
              {s.lastOkAt && (
                <div>
                  {/* Damga MARKANIN diliminde (BetBooker=Akra) — plan da o dilimde okunuyor. */}
                  Son okuma: <span style={{ color: 'var(--text)' }}>{fmtStamp(s.lastOkAt, tzOfBrands([b]), true)}</span>
                </div>
              )}
              <div>
                {s.weekStart && (
                  <>
                    Hafta: <span style={{ color: 'var(--text)' }}>{s.weekStart}</span>
                    {'  ·  '}
                  </>
                )}
                Kayıt: <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{String(s.rows ?? 0)}</span>
                {'  ·  '}
                Kişi: <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{String(s.agents ?? 0)}</span>
              </div>
              {(s.blankCells ?? 0) > 0 && (
                // Boş hücre = bilgi yok ⇒ o güne satır yazılmaz (izinli DEĞİL). Yarım
                // doldurulmuş bir plan sessizce az iş yapar; sayıyı göstermek bunu
                // senkron arızası gibi görünmekten çıkarır.
                <div>
                  {'Boş hücre: ' + String(s.blankCells) + ' (satır üretmez)'}
                </div>
              )}
              {s.unmatched && s.unmatched.length > 0 && (
                <div style={{ color: '#e0a23c' }}>
                  {'Eşleşmeyen isim: ' + s.unmatched.join(', ')}
                </div>
              )}
              {s.warnings && s.warnings.length > 0 && (
                <div style={{ color: 'var(--faint)', fontSize: '11px' }}>{s.warnings.join(' · ')}</div>
              )}
            </div>
          </div>
        );
      })}

      {msg && <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--dim)' }}>{msg}</div>}
    </div>
  );
}

// ── Durum kategorileri ───────────────────────────────────────────────────────

function LazinessCard({
  title,
  sub,
  rows,
  onToggle,
}: {
  title: string;
  sub: string;
  rows: LazinessEntry[];
  onToggle: (key: StatusKey, included: boolean) => void;
}) {
  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: '11px', color: 'var(--faint)' }}>{sub}</div>
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          onClick={() => onToggle(r.key, !r.included)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            padding: '11px 16px',
            borderBottom: '1px solid var(--border)',
            cursor: 'pointer',
          }}
        >
          <div style={checkboxStyle(r.included)}>{r.included && <Icon name="check" size={15} color="#fff" />}</div>
          <span style={dotStyle(r.color, 8)} />
          <span style={{ fontSize: '13px', color: 'var(--text)' }}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

function StatusesTab({
  laziness,
  onToggle,
}: {
  laziness: LazinessConfig;
  onToggle: (lang: Lang, key: StatusKey, included: boolean) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '14px' }}>
      <LazinessCard
        title="Tembellik metriği — Türkçe"
        sub="İşaretli sonuçlar metriğe dahildir"
        rows={laziness.tr}
        onToggle={(k, inc) => onToggle('tr', k, inc)}
      />
      <LazinessCard
        title="Tembellik metriği — BetBooker"
        sub="İngilizce sonuç eşdeğerleri"
        rows={laziness.en}
        onToggle={(k, inc) => onToggle('en', k, inc)}
      />
    </div>
  );
}

// ── Kullanıcı modali ─────────────────────────────────────────────────────────

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--dim)',
  marginBottom: '7px',
  letterSpacing: '.04em',
} as const;

function UserModal({
  mode,
  username,
  setUsername,
  password,
  setPassword,
  role,
  setRole,
  brands,
  toggleBrand,
  active,
  toggleActive,
  newPass,
  setNewPass,
  resetUserName,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  mode: UmMode;
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  role: Role;
  setRole: (v: Role) => void;
  brands: BrandId[];
  toggleBrand: (id: BrandId) => void;
  active: boolean;
  toggleActive: () => void;
  newPass: string;
  setNewPass: (v: string) => void;
  resetUserName: string;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const title = mode === 'new' ? 'Yeni Kullanıcı' : mode === 'edit' ? 'Kullanıcı Düzenle' : 'Şifre Sıfırla';
  return (
    <Modal onClose={onClose} maxWidth={440}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '18px' }}>{title}</div>
      {mode !== 'reset' && (
        <>
          <label style={labelStyle}>KULLANICI ADI</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={mode === 'edit'}
            autoFocus
            placeholder="kullanıcı adı"
            style={{ ...inputStyle, marginBottom: '14px' }}
          />
          {mode === 'new' && (
            <>
              <label style={labelStyle}>ŞİFRE</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="en az 6 karakter"
                style={{ ...inputStyle, marginBottom: '14px' }}
              />
            </>
          )}
          <label style={labelStyle}>ROL</label>
          <div style={{ position: 'relative', marginBottom: '14px' }}>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                width: '100%',
                background: 'var(--inset)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                padding: '11px 34px 11px 13px',
                color: 'var(--text)',
                fontSize: '13.5px',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="supervisor" style={{ background: 'var(--surface)' }}>
                Süpervizör
              </option>
              <option value="admin" style={{ background: 'var(--surface)' }}>
                Yönetici
              </option>
            </select>
            <Icon
              name="expand_more"
              size={18}
              color="var(--faint)"
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            />
          </div>
          <label style={{ ...labelStyle, marginBottom: '8px' }}>ATANMIŞ MARKALAR</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {/* Chips = the assignable catalog (ALL_BRANDS) PLUS any brand this user already
                carries that has since been RETIRED from the catalog (e.g. betbooker after
                2026-09-09). A new user has no brands, so a retired brand is never OFFERED for
                a NEW account — it only surfaces on EDIT, so an admin can deselect a legacy
                assignment that the hidden-chip catalog could otherwise never clear. */}
            {[
              ...ALL_BRANDS,
              ...brands
                .filter((b) => !ALL_BRANDS.some((x) => x.id === b))
                .map((b) => ({ id: b, name: brandLabel(b) })),
            ].map((b) => (
              <button key={b.id} onClick={() => toggleBrand(b.id)} style={chipStyle(brands.includes(b.id))}>
                {b.name}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '22px' }}>
            <div onClick={toggleActive} style={switchStyle(active)}>
              <div style={knobStyle(active)} />
            </div>
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>Aktif</span>
          </div>
        </>
      )}
      {mode === 'reset' && (
        <>
          <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px' }}>
            <span style={{ color: 'var(--text)', fontWeight: 600 }}>{resetUserName}</span> için yeni şifre belirleyin.
          </div>
          <label style={labelStyle}>YENİ ŞİFRE</label>
          <input
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            type="password"
            autoFocus
            placeholder="yeni şifre"
            style={{ ...inputStyle, marginBottom: '22px' }}
          />
        </>
      )}
      {/* Hata gelince modal AÇIK kalır: girilen değerler durur, admin düzeltip yeniden kaydeder. */}
      {error ? <ErrorLine text={error} style={{ marginBottom: '14px' }} /> : null}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} style={{ ...ghostBtnStyle, flex: 1 }}>
          İptal
        </button>
        <button onClick={onSubmit} disabled={busy} style={{ ...primaryBtnStyle, flex: 2 }}>
          {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </div>
    </Modal>
  );
}

// ── Birleştirme modali ───────────────────────────────────────────────────────

function MergeModal({
  temp,
  agents,
  target,
  setTarget,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  temp: AgentRecord;
  agents: AgentRecord[];
  target: string;
  setTarget: (v: string) => void;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const options = [{ value: '', label: 'Temsilci seçin…' }].concat(
    agents.filter((a) => !a.temp && !a.removed).map((a) => ({ value: a.id, label: joinDot(a.name, a.department) })),
  );
  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '16px' }}>
        <div
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            background: 'rgba(224,162,60,.14)',
            border: '1px solid rgba(224,162,60,.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon name="merge" size={20} color="#e0a23c" />
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Geçici temsilciyi birleştir</div>
          <div style={{ fontSize: '12px', color: 'var(--dim)' }}>
            {temp.name} · {temp.darkCode}
          </div>
        </div>
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px', lineHeight: 1.5 }}>
        Bu geçici kaydın alias'ları ve markaları seçilen mevcut temsilciye taşınır, geçici kayıt silinir.
      </div>
      <label style={labelStyle}>HEDEF TEMSİLCİ</label>
      <div style={{ marginBottom: '22px' }}>
        <Select
          value={target}
          onChange={setTarget}
          options={options}
          autoFocus
          fullWidth
          style={{ background: 'var(--inset)', padding: '11px 34px 11px 13px', fontSize: '13.5px', fontWeight: 400 }}
        />
      </div>
      {error ? <ErrorLine text={error} style={{ marginBottom: '14px' }} /> : null}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} style={{ ...ghostBtnStyle, flex: 1 }}>
          İptal
        </button>
        <button onClick={onSubmit} disabled={busy || !target} style={{ ...primaryBtnStyle, flex: 2 }}>
          {busy ? 'Birleştiriliyor…' : 'Birleştir'}
        </button>
      </div>
    </Modal>
  );
}

/** İşten ayrıldı (src/api/offboard.js, sahip 2026-11-02). TEK basış: panel satırı ayrılış gününden
 *  itibaren kalkar (öncesi raporlarda kalır), panel girişi her markada kapanır, QMS hesabı kapanır.
 *  Satır SİLİNMEZ; "Kaldırılanlar"dan geri alınır. Sonuç adım adım gösterilir; hata/uyarı varsa
 *  "Tekrar dene" yalnız eksik kalanı yapar (adımlar idempotent). */
function RemoveModal({
  agent,
  busy,
  error,
  leftOn,
  maxDay,
  reason,
  confirmName,
  steps,
  onLeftOn,
  onReason,
  onConfirmName,
  onClose,
  onSubmit,
}: {
  agent: AgentRecord;
  busy: boolean;
  error: string;
  leftOn: string;
  maxDay: string;
  reason: OffboardReason;
  confirmName: string;
  steps: PanelSyncStep[] | null;
  onLeftOn: (v: string) => void;
  onReason: (v: OffboardReason) => void;
  onConfirmName: (v: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const done = !!steps;
  const failed = !!steps && steps.some((x) => x.status === 'error' || x.status === 'warn');
  const labelSt: CSSProperties = { fontSize: '11px', fontWeight: 600, color: 'var(--dim)', letterSpacing: '.04em', marginBottom: '6px' };
  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '16px' }}>
        <div
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            background: 'rgba(239,93,93,.14)',
            border: '1px solid rgba(239,93,93,.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon name="person_off" size={20} color="#ef5d5d" />
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>İşten ayrıldı</div>
          <div style={{ fontSize: '12px', color: 'var(--dim)' }}>{joinDot(agent.name, agent.darkCode)}</div>
        </div>
      </div>
      {done ? (
        <div style={{ marginBottom: '18px' }}>
          <BrandSteps steps={steps} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '16px', lineHeight: 1.5 }}>
            Tek seferde: ayrılış gününden itibaren Canlı Durum ve raporlardan çıkar (öncesi raporlarda kalır), açık
            uyarıları kapanır, eklenti girişi kapanır ve QMS hesabı kapanır. Çağrı, mola ve DARK geçmişi silinmez —
            Kaldırılanlar listesinden geri alınabilir.
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}>
              <div style={labelSt}>AYRILIŞ TARİHİ</div>
              <input type="date" value={leftOn} max={maxDay} onChange={(e) => onLeftOn(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <div style={labelSt}>SEBEP</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => onReason('left')} style={chipStyle(reason === 'left')}>
                  Ayrıldı
                </button>
                <button onClick={() => onReason('fired')} style={chipStyle(reason === 'fired')}>
                  Çıkarıldı
                </button>
              </div>
            </div>
          </div>
          <div style={labelSt}>ONAY İÇİN ADINI YAZIN</div>
          <input
            value={confirmName}
            onChange={(e) => onConfirmName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
            placeholder={agent.name}
            autoFocus
            style={{ ...inputStyle, marginBottom: '14px' }}
          />
          <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '18px', lineHeight: 1.45 }}>
            Elle kalanlar: getircall'da dahiliyi boşaltın · BackOffice kullanıcısını kapatın.
          </div>
        </>
      )}
      {error ? <ErrorLine text={error} style={{ marginBottom: '14px' }} /> : null}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} style={{ ...ghostBtnStyle, flex: 1 }}>
          {done ? 'Kapat' : 'İptal'}
        </button>
        {!done || failed ? (
          <button
            onClick={onSubmit}
            disabled={busy}
            style={{
              ...primaryBtnStyle,
              flex: 2,
              background: 'rgba(239,93,93,.14)',
              border: '1px solid rgba(239,93,93,.4)',
              color: '#ef5d5d',
            }}
          >
            {busy ? 'İşleniyor…' : failed ? 'Tekrar dene' : 'İşten ayrıldı'}
          </button>
        ) : null}
      </div>
    </Modal>
  );
}

/** Aktif anahtarı KAPATILIRKEN sorulur: ayrılan biri "pasif" yapılırsa geçmiş raporlardan da düşer
 *  (NAZLI, 2026-11-02) — ayrıldıysa doğru yol İşten ayrıldı. */
function DeactivateAskModal({ agent, onLeft, onOnlyInactive, onClose }: {
  agent: AgentRecord;
  onLeft: () => void;
  onOnlyInactive: () => void;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>İşten mi ayrıldı?</div>
      <div style={{ fontSize: '12px', color: 'var(--dim)', marginBottom: '14px' }}>{joinDot(agent.name, agent.darkCode)}</div>
      <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '20px', lineHeight: 1.5 }}>
        Ayrıldıysa "İşten ayrıldı" kullanın: geçmiş raporlarda kalır, eklenti ve QMS girişi de kapanır. Pasif yapmak
        temsilciyi geçmiş raporlardan da çıkarır ve girişlerine dokunmaz.
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onOnlyInactive} style={{ ...ghostBtnStyle, flex: 1 }}>
          Sadece pasif yap
        </button>
        <button onClick={onLeft} style={{ ...primaryBtnStyle, flex: 1 }}>
          İşten ayrıldı
        </button>
      </div>
    </Modal>
  );
}

// ── Sayfa ────────────────────────────────────────────────────────────────────

export default function Settings({ brands, allBrands }: { brands: BrandId[]; allBrands: BrandId[] }): JSX.Element {
  const [stTab, setStTab] = useState<StTab>('users');
  const [stUsers, setStUsers] = useState<PanelUser[]>([]);
  const [stAgents, setStAgents] = useState<AgentRecord[]>([]);
  const [stThresholds, setStThresholds] = useState<Thresholds | null>(null);
  const [stLaziness, setStLaziness] = useState<LazinessConfig | null>(null);
  const [stLoading, setStLoading] = useState(true);
  const [stAliasInput, setStAliasInput] = useState<Record<string, string>>({});
  const [stRenameInput, setStRenameInput] = useState<Record<string, string>>({});
  // Satır bazlı mutasyon hataları (kullanıcı adı / temsilci id anahtarlı) — bir satırın
  // hatası diğerlerini gizlemesin diye tek bir global mesaj kullanılmıyor.
  const [stUserMsg, setStUserMsg] = useState<Record<string, string>>({});
  const [stAgentMsg, setStAgentMsg] = useState<Record<string, string>>({});
  const [stAgentSync, setStAgentSync] = useState<Record<string, PanelSyncResult>>({});
  const [stSyncBusy, setStSyncBusy] = useState<string | null>(null);
  const { toggle: toggleAgentExpand, isOpen } = useExpanded();

  const [umOpen, setUmOpen] = useState(false);
  const [umMode, setUmMode] = useState<UmMode>('new');
  const [umUser, setUmUser] = useState<string | null>(null);
  const [umUsername, setUmUsername] = useState('');
  const [umPassword, setUmPassword] = useState('');
  const [umRole, setUmRole] = useState<Role>('supervisor');
  const [umBrands, setUmBrands] = useState<BrandId[]>([]);
  const [umActive, setUmActive] = useState(true);
  const [umNewPass, setUmNewPass] = useState('');
  const [umBusy, setUmBusy] = useState(false);
  const [umError, setUmError] = useState('');

  const [mgOpen, setMgOpen] = useState(false);
  const [mgTemp, setMgTemp] = useState<AgentRecord | null>(null);
  const [mgTarget, setMgTarget] = useState('');
  const [mgBusy, setMgBusy] = useState(false);
  const [mgError, setMgError] = useState('');

  const [rmAgent, setRmAgent] = useState<AgentRecord | null>(null);
  const [rmBusy, setRmBusy] = useState(false);
  const [rmError, setRmError] = useState('');
  const [rmLeftOn, setRmLeftOn] = useState('');
  const [rmReason, setRmReason] = useState<OffboardReason>('left');
  const [rmConfirm, setRmConfirm] = useState('');
  const [rmSteps, setRmSteps] = useState<PanelSyncStep[] | null>(null);
  const [daAgent, setDaAgent] = useState<AgentRecord | null>(null);

  // İskelet yalnızca ilk yüklemede: eşikler geldiyse tazelemeler sessiz kalır.
  const thRef = useRef<Thresholds | null>(null);

  const loadSettings = useCallback(async () => {
    setStLoading(!thRef.current);
    const [users, agents, thresholds, laziness] = await Promise.all([
      getUsers(),
      getAgentRecords(),
      getThresholds(),
      getLazinessConfig(),
    ]);
    thRef.current = thresholds;
    setStUsers(users);
    setStAgents(agents);
    setStThresholds(thresholds);
    setStLaziness(laziness);
    setStLoading(false);
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEscape(umOpen || mgOpen || !!rmAgent, () => {
    if (umOpen) setUmOpen(false);
    else if (mgOpen) setMgOpen(false);
    else if (rmAgent) setRmAgent(null);
  });

  /** Satır mesajını yazar (null → satırdan siler). */
  const noteUser = (username: string, msg: string | null) =>
    setStUserMsg((st) => {
      const next = { ...st };
      if (msg) next[username] = msg;
      else delete next[username];
      return next;
    });
  const noteAgent = (id: string, msg: string | null) =>
    setStAgentMsg((st) => {
      const next = { ...st };
      if (msg) next[id] = msg;
      else delete next[id];
      return next;
    });

  const toggleUserActive = async (u: PanelUser) => {
    noteUser(u.username, await runMutation(() => updateUser(u.username, { active: !u.active })));
    await loadSettings();
  };
  const openNewUser = () => {
    setUmOpen(true);
    setUmMode('new');
    setUmUser(null);
    setUmUsername('');
    setUmPassword('');
    setUmRole('supervisor');
    setUmBrands([]);
    setUmActive(true);
    setUmError('');
  };
  const openEditUser = (u: PanelUser) => {
    setUmOpen(true);
    setUmMode('edit');
    setUmUser(u.username);
    setUmUsername(u.username);
    setUmRole(u.role);
    setUmBrands(u.brands.slice());
    setUmActive(u.active);
    setUmError('');
  };
  const openResetUser = (u: PanelUser) => {
    setUmOpen(true);
    setUmMode('reset');
    setUmUser(u.username);
    setUmUsername(u.username);
    setUmNewPass('');
    setUmError('');
  };
  const submitUserModal = async () => {
    if (umBusy) return;
    setUmBusy(true);
    try {
      const err = await runMutation(() => {
        if (umMode === 'new') {
          return createUser({ username: umUsername, password: umPassword, role: umRole, brands: umBrands, active: umActive });
        }
        if (umMode === 'edit' && umUser) return updateUser(umUser, { role: umRole, brands: umBrands, active: umActive });
        if (umMode === 'reset' && umUser) return resetPassword(umUser, umNewPass);
        return Promise.resolve({ ok: true });
      });
      // Mesaj loadSettings'ten ÖNCE yazılır: tazeleme de patlarsa hata yine görünsün.
      setUmError(err || '');
      await loadSettings();
      if (!err) setUmOpen(false);
    } finally {
      setUmBusy(false);
    }
  };

  const setAgentDept = async (id: string, dept: string) => {
    noteAgent(id, await runMutation(() => updateAgentRecord(id, { department: dept })));
    await loadSettings();
  };
  const toggleAgentBrandRec = async (id: string, brand: BrandId) => {
    const a = stAgents.find((x) => x.id === id);
    if (!a) return;
    const brands = a.brands.includes(brand) ? a.brands.filter((b) => b !== brand) : [...a.brands, brand];
    setStSyncBusy(id);
    let sync: PanelSyncResult | undefined;
    noteAgent(id, await runMutation(async () => {
      const r = await updateAgentRecord(id, { brands });
      sync = r.panelSync;
      return r;
    }));
    const got = sync;
    if (got) setStAgentSync((st) => ({ ...st, [id]: got }));
    setStSyncBusy(null);
    await loadSettings();
  };
  const syncAgentPanelNow = async (id: string) => {
    if (stSyncBusy) return;
    setStSyncBusy(id);
    let sync: PanelSyncResult | undefined;
    noteAgent(id, await runMutation(async () => {
      const r = await syncAgentPanel(id);
      sync = r.panelSync;
      return r;
    }));
    const got = sync;
    if (got) setStAgentSync((st) => ({ ...st, [id]: got }));
    setStSyncBusy(null);
    await loadSettings();
  };
  const toggleAgentActive = async (id: string, ask = true) => {
    const a = stAgents.find((x) => x.id === id);
    if (!a) return;
    // Kapatırken sor: ayrılan biri için doğru yol İşten ayrıldı (geçmiş raporlarda kalır).
    if (ask && a.active) {
      setDaAgent(a);
      return;
    }
    noteAgent(id, await runMutation(() => updateAgentRecord(id, { active: !a.active })));
    await loadSettings();
  };
  // Takım lideri: bu temsilciden hiçbir uyarı üretilmez ve AÇILDIĞINDA açık uyarıları da
  // kapanır (sunucu tarafında, api/settings.js) — bu yüzden listeyi yeniden yüklüyoruz.
  const toggleAgentTeamLead = async (id: string) => {
    const a = stAgents.find((x) => x.id === id);
    if (!a) return;
    noteAgent(id, await runMutation(() => updateAgentRecord(id, { teamLead: !a.teamLead })));
    await loadSettings();
  };
  const setRenameInput = (id: string, val: string) => setStRenameInput((st) => ({ ...st, [id]: val }));
  const renameAgent = async (id: string, name: string) => {
    const v = name.trim();
    const a = stAgents.find((x) => x.id === id);
    if (!v || !a || v === a.name) return;
    const err = await runMutation(() => updateAgentRecord(id, { name: v }));
    noteAgent(id, err);
    // Yazılan ad yalnızca BAŞARIDA temizlenir; hatada alanda kalsın ki düzeltilebilsin.
    if (!err) {
      setStRenameInput((st) => {
        const next = { ...st };
        delete next[id];
        return next;
      });
    }
    await loadSettings();
  };
  const confirmAgent = async (id: string) => {
    noteAgent(id, await runMutation(() => updateAgentRecord(id, { temp: false })));
    await loadSettings();
  };
  const setAliasInput = (key: string, val: string) => setStAliasInput((st) => ({ ...st, [key]: val }));
  const addAlias = async (id: string, brand: BrandId) => {
    const key = id + '|' + brand;
    const val = (stAliasInput[key] || '').trim();
    if (!val) return;
    const err = await runMutation(() => addAgentAlias(id, brand, val));
    noteAgent(id, err);
    if (!err) setStAliasInput((st) => ({ ...st, [key]: '' }));
    await loadSettings();
  };
  const removeAlias = async (id: string, brand: BrandId, name: string) => {
    noteAgent(id, await runMutation(() => removeAgentAlias(id, brand, name)));
    await loadSettings();
  };

  const openMerge = (agent: AgentRecord) => {
    setMgOpen(true);
    setMgTemp(agent);
    setMgTarget('');
    setMgError('');
  };
  const submitMerge = async () => {
    if (mgBusy || !mgTarget || !mgTemp) return;
    setMgBusy(true);
    try {
      const err = await runMutation(() => mergeAgent(mgTemp.id, mgTarget));
      setMgError(err || '');
      await loadSettings();
      if (err) return;
      setMgOpen(false);
      setMgTemp(null);
    } finally {
      setMgBusy(false);
    }
  };

  // Ayrılış günü İstanbul günüdür (sunucu removed_at'ı o günün 00:00 İstanbul'una basar).
  const todayIstanbul = () => todayInTz('Europe/Istanbul');
  const openRemove = (agent: AgentRecord) => {
    setRmAgent(agent);
    setRmError('');
    setRmLeftOn(todayIstanbul());
    setRmReason('left');
    setRmConfirm('');
    setRmSteps(null);
  };
  const submitRemove = async () => {
    if (rmBusy || !rmAgent) return;
    setRmBusy(true);
    try {
      const r = await offboardAgentRecord(rmAgent.id, { leftOn: rmLeftOn, reason: rmReason, confirmName: rmConfirm });
      setRmError(r.error || '');
      if (r.steps) setRmSteps(r.steps);
      await loadSettings();
    } catch {
      setRmError('İşlem başarısız');
    } finally {
      setRmBusy(false);
    }
  };
  // Geri al: QMS hesabı da yeniden açılır; adımlar satırın PANEL HESABI alanında görünür.
  const restoreAgent = async (id: string) => {
    let steps: PanelSyncStep[] | undefined;
    noteAgent(id, await runMutation(async () => {
      const r = await restoreAgentRecord(id);
      steps = r.steps;
      return r;
    }));
    const got = steps;
    if (got) setStAgentSync((st) => ({ ...st, [id]: { ok: !got.some((x) => x.status === 'error'), steps: got } }));
    await loadSettings();
  };

  // Eşik ve tembellik: önce yerel (iyimser) yaz, sonra sunucuya gönder — ama sonucu OKU.
  // Soft hatalar HTTP 200 + {ok:false} olarak döner (api/settings.js), bu yüzden sonuç
  // okunmayınca reddedilen bir değişiklik ekranda kabul edilmiş gibi duruyordu: panel canlı
  // yapılandırmadan sessizce sapıyor, uyarılar sanılan eşikle değil eski eşikle üretilmeye
  // devam ediyordu. Hatada iyimser yazma sunucudan taze okunarak geri alınır ve şerit basılır.
  // (runMutation is not usable here: updateThresholds resolves to the THRESHOLDS object, which
  // has no `ok` field, so a success would read as a failure. These endpoints reject by status,
  // and http() throws on any non-2xx — so catch, don't inspect.)
  const [stConfigError, setStConfigError] = useState('');
  const setThreshold = async (key: keyof Thresholds, value: string) => {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setStThresholds((st) => (st ? { ...st, [key]: v } : st));
    try {
      const fresh = await updateThresholds({ [key]: v });
      setStThresholds(fresh);       // server truth wins (it may clamp)
      setStConfigError('');
    } catch (e) {
      setStConfigError(e instanceof Error ? e.message : FALLBACK_ERROR);
      await loadSettings();         // undo the optimistic write
    }
  };
  const toggleLaziness = async (lang: Lang, key: StatusKey, included: boolean) => {
    setStLaziness((st) => (st ? { ...st, [lang]: st[lang].map((x) => (x.key === key ? { ...x, included } : x)) } : st));
    try {
      const res = await setLazinessIncluded(lang, key, included);
      if (res && res.ok === false) throw new Error(FALLBACK_ERROR);
      setStConfigError('');
    } catch (e) {
      setStConfigError(e instanceof Error ? e.message : FALLBACK_ERROR);
      await loadSettings();
    }
  };

  const loading = stLoading && !stThresholds;
  const ready = !loading && !!stThresholds;

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {TABS.map(([key, label, icon]) => (
          <button key={key} onClick={() => setStTab(key)} style={segStyle(stTab === key)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
              <Icon name={icon} size={16} />
              {label}
            </span>
          </button>
        ))}
      </div>

      {loading && <Skeleton heights={[56, 200]} />}

      {ready && (
        <>
          {stTab === 'users' && (
            <UsersTab
              users={stUsers}
              rowMsg={stUserMsg}
              onOpenNew={openNewUser}
              onToggleActive={toggleUserActive}
              onEdit={openEditUser}
              onReset={openResetUser}
            />
          )}
          {stTab === 'agents' && (
            <AgentsTab
              agents={stAgents}
              brands={brands}
              allBrands={allBrands}
              rowMsg={stAgentMsg}
              isOpen={isOpen}
              onToggleExpand={toggleAgentExpand}
              onDept={setAgentDept}
              onToggleActive={toggleAgentActive}
              onToggleTeamLead={toggleAgentTeamLead}
              onToggleBrand={toggleAgentBrandRec}
              panelSync={stAgentSync}
              panelSyncBusy={stSyncBusy}
              onPanelSync={syncAgentPanelNow}
              renameInput={stRenameInput}
              onRenameInput={setRenameInput}
              onRename={renameAgent}
              onConfirm={confirmAgent}
              aliasInput={stAliasInput}
              onAliasInput={setAliasInput}
              onAddAlias={addAlias}
              onRemoveAlias={removeAlias}
              onMerge={openMerge}
              onRemove={openRemove}
              onRestore={restoreAgent}
            />
          )}
          {stTab === 'thresholds' && stThresholds && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {stConfigError && <ErrorLine text={'Ayar kaydedilemedi — ' + stConfigError} />}
              <ThresholdsTab thresholds={stThresholds} onChange={setThreshold} />
              <SchedulePlanCard />
            </div>
          )}
          {stTab === 'statuses' && stLaziness && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {stConfigError && <ErrorLine text={'Ayar kaydedilemedi — ' + stConfigError} />}
              <StatusesTab laziness={stLaziness} onToggle={toggleLaziness} />
            </div>
          )}
        </>
      )}

      {umOpen && (
        <UserModal
          mode={umMode}
          username={umUsername}
          setUsername={setUmUsername}
          password={umPassword}
          setPassword={setUmPassword}
          role={umRole}
          setRole={setUmRole}
          brands={umBrands}
          toggleBrand={(id) => setUmBrands((st) => (st.includes(id) ? st.filter((x) => x !== id) : [...st, id]))}
          active={umActive}
          toggleActive={() => setUmActive((v) => !v)}
          newPass={umNewPass}
          setNewPass={setUmNewPass}
          resetUserName={umUser || ''}
          busy={umBusy}
          error={umError}
          onClose={() => setUmOpen(false)}
          onSubmit={submitUserModal}
        />
      )}

      {mgOpen && mgTemp && (
        <MergeModal
          temp={mgTemp}
          agents={stAgents}
          target={mgTarget}
          setTarget={setMgTarget}
          busy={mgBusy}
          error={mgError}
          onClose={() => setMgOpen(false)}
          onSubmit={submitMerge}
        />
      )}

      {rmAgent && (
        <RemoveModal
          agent={rmAgent}
          busy={rmBusy}
          error={rmError}
          leftOn={rmLeftOn}
          maxDay={todayIstanbul()}
          reason={rmReason}
          confirmName={rmConfirm}
          steps={rmSteps}
          onLeftOn={setRmLeftOn}
          onReason={setRmReason}
          onConfirmName={setRmConfirm}
          onClose={() => setRmAgent(null)}
          onSubmit={submitRemove}
        />
      )}

      {daAgent && (
        <DeactivateAskModal
          agent={daAgent}
          onClose={() => setDaAgent(null)}
          onLeft={() => {
            const a = daAgent;
            setDaAgent(null);
            openRemove(a);
          }}
          onOnlyInactive={() => {
            const id = daAgent.id;
            setDaAgent(null);
            void toggleAgentActive(id, false);
          }}
        />
      )}
    </>
  );
}
